import { afterApprovalStatuses, afterRejectionStatuses, approvedStatuses } from '@/lib/constants/med-requests'
import { isBeforeClinicalReview } from '@/lib/med-requests'
import type { MedicationRequestStore } from '@/lib/stores/medication-request'
import { isMpsMedRequest } from '@/lib/utils'
import type { MedRequest } from '@/types/data'
import type { MedRequestMutationFnProps } from './action'
import { lookupHashesOf } from './mps-sync-plan'

/**
 * Every medication of an MPS family is a med request of its own and the whole family sits in
 * clinical review together (the queue shows a single entry for it — see dedupeMpsMedRequests),
 * so a decision taken on the opened request has to be sent to each sibling too or they stay
 * pending. These are the family rows still awaiting that decision.
 */
export const pendingMpsFamilyRows = (medRequest: MedRequest): MedRequest[] => {
  // approvedStatuses adds Pharmacist Check, where PHP refuses an approve, a reject or a hold.
  const settledStages = [...afterApprovalStatuses, ...approvedStatuses, ...afterRejectionStatuses].map(stage =>
    stage.toLowerCase()
  )

  // Only the medication orders take a med-req /update — the 'mps' rows are the subscription's
  // own mirror items (the swap/remove targets, via mpsHash), not actionable med requests.
  return (medRequest.mpsMedRequests ?? []).filter(
    row => row.evaroType?.toLowerCase() !== 'mps' && !settledStages.includes(row.stage?.toLowerCase() ?? '')
  )
}

type MpsApprovalPicks = Pick<MedicationRequestStore, 'extraTreatments' | 'removedTreatments' | 'droppedProductHashes'>

/**
 * The pending family rows an MPS approve sends, leaving out those whose treatment is queued for removal
 * or already taken off the subscription. A card's own landed add is kept even when it re-picks a dropped product.
 */
export const mpsApprovalRowsOf = (
  medRequest: MedRequest,
  { extraTreatments, removedTreatments, droppedProductHashes }: MpsApprovalPicks
): MedRequest[] => {
  const addedRowIds = extraTreatments.map(({ persistedMedReqId }) => persistedMedReqId)
  const isLeavingSubscription = (row: MedRequest) =>
    removedTreatments.some(
      ({ itemHash, medication }) =>
        (!!itemHash && itemHash === row.mpsHash) || lookupHashesOf(medication).includes(row.orderedItem?.id ?? '')
    ) ||
    (droppedProductHashes.includes(row.orderedItem?.id ?? '') && !addedRowIds.includes(row.id))

  return pendingMpsFamilyRows(medRequest).filter(row => !isLeavingSubscription(row))
}

/**
 * Why an MPS approve has to wait, or null: a row it sends is still before Clinical Review, which PHP
 * won't approve, or a treatment the sync added isn't listed yet (still Created, which NodeAPI leaves
 * out). Quoted names: they carry commas ("Evorel 50, 8 Patches").
 */
export const mpsApprovalWaitReasonOf = (medRequest: MedRequest | undefined, picks: MpsApprovalPicks): string | null => {
  if (!medRequest || !isMpsMedRequest(medRequest)) return null

  const familyRowIds = new Set([medRequest, ...(medRequest.mpsMedRequests ?? [])].map(row => row.id))
  const waitReasons = [
    // The prescribed name stays empty until a prescriber picks one.
    ...[medRequest, ...mpsApprovalRowsOf(medRequest, picks)]
      .filter(isBeforeClinicalReview)
      .map(row => `"${row.orderedItem?.name || row.orderMedNameString}" is in ${row.stage}`),
    ...picks.extraTreatments
      .filter(({ persistedMedReqId }) => persistedMedReqId && !familyRowIds.has(persistedMedReqId))
      .map(({ persistedMedication }) => `"${persistedMedication?.productName}" hasn't reached the automated checks yet`)
  ]

  return waitReasons.length
    ? `${waitReasons.join(' and ')}. Approve once every treatment reaches Clinical Review.`
    : null
}

/**
 * The reject / follow-up (hold) actions for the treatments of an MPS family beyond the opened
 * request: one per sibling med request still pending, each carrying only the id and the reason.
 * Unlike approve, nothing is synced to the subscription first — a decision only reaches the
 * treatments that already exist as med requests, so a treatment card added locally (no med
 * request id yet) is left alone; the changes on it stay in the store until an approve.
 *
 * Everything patient-facing stays on the opened request's own action: the rejection / hold
 * conversation, the CS contact flag, notes, SCR payload. `suppressCsEmailForMps` keeps the
 * server's per-request hold email off the siblings, so a family hold raises a single CS task.
 * Non-MPS requests get an empty list, so callers can spread this unconditionally.
 */
export const mpsFamilyDecisionActions = (
  medRequest: MedRequest,
  decision: { action: 'reject' | 'hold'; reason: string }
): MedRequestMutationFnProps[] => {
  if (!isMpsMedRequest(medRequest)) return []

  return pendingMpsFamilyRows(medRequest).map(row => ({ id: row.id, ...decision, suppressCsEmailForMps: true }))
}

/**
 * Fires the updates one at a time — the family's requests all mutate the shared subscription
 * order, so parallel updates could race on it — and stops at the first failure so the rows not
 * yet actioned stay pending for a retry. Failures surface via the mutation's own onError toast,
 * which is why the chain itself swallows them; the returned counts let the caller decide
 * whether to navigate away or keep the page for a retry.
 */
export const runMedRequestActionChain = async (
  send: (variables: MedRequestMutationFnProps) => Promise<unknown>,
  actions: MedRequestMutationFnProps[]
): Promise<{ ok: boolean; completed: number; total: number }> => {
  let completed = 0
  for (const variables of actions) {
    try {
      await send(variables)
      completed++
    } catch {
      break
    }
  }
  return { ok: completed === actions.length, completed, total: actions.length }
}
